import { useEffect, useState } from 'react';
import type { PieceView, WatchPiece } from '@/data/catalog';

type Props = {
  piece: WatchPiece;
};

export function AngleStage({ piece }: Props) {
  const views = piece.views;
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    setIdx(0);
  }, [piece.id]);

  // Warm all angles for the active piece so ←/→ feels instant.
  useEffect(() => {
    const loaders = views.map((v) => {
      const img = new Image();
      img.decoding = 'async';
      img.src = v.src;
      return img;
    });
    return () => {
      for (const img of loaders) img.src = '';
    };
  }, [piece.id, views]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setIdx((i) => (i - 1 + views.length) % views.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setIdx((i) => (i + 1) % views.length);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [views.length]);

  const view: PieceView = views[idx] ?? views[0];

  return (
    <div className="angle-stage">
      <div className="angle-frame">
        <img
          key={`${piece.id}-${view.angle}`}
          className="angle-shot"
          src={view.src}
          alt={`${piece.nameKo} · ${view.labelKo}`}
          draggable={false}
          decoding="async"
          fetchPriority="high"
        />
      </div>

      <div className="angle-controls" role="group" aria-label="시점">
        <button
          type="button"
          className="angle-nav"
          aria-label="이전 시점"
          onClick={() => setIdx((i) => (i - 1 + views.length) % views.length)}
        >
          ‹
        </button>
        <div className="angle-dots">
          {views.map((v, i) => (
            <button
              key={v.angle}
              type="button"
              className={i === idx ? 'angle-dot is-on' : 'angle-dot'}
              aria-label={v.labelKo}
              aria-pressed={i === idx}
              onClick={() => setIdx(i)}
            >
              {v.labelKo}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="angle-nav"
          aria-label="다음 시점"
          onClick={() => setIdx((i) => (i + 1) % views.length)}
        >
          ›
        </button>
      </div>
      <p className="angle-hint">← → 키로 시점 전환</p>
    </div>
  );
}
